//
// ユーザーリスト編集モーダル
//
// src/components/userListEditModal.js
//

import React, { useEffect, useState } from "react";
import "../styles/userListEditModal.css";
import * as XLSX from 'xlsx';
import DoubleConfirmModal from './doubleConfirmModal';


function UserListEditModal({ onClose, currentUser }) {
  const [users, setUsers] = useState([]);
  const [originalUsers, setOriginalUsers] = useState([]);
  const [selectedChanges, setSelectedChanges] = useState({}); // 変更マーク用
  const [confirmConfig, setConfirmConfig] = useState(null);

  // 新規追加登録用 --------------------------------------------------
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  const initialNewUser = { 
    id: "KDU93103", // prefix固定 
    suffix: "", // 入力部分 
    name: "未設定", 
    yomigana: "", 
    company: "", 
    role: "利用者", 
    note: "",
  }; 
  const [newUser, setNewUser] = useState(initialNewUser);

  // 日本時間で今日の日付 (YYYY-MM-DD) --------------------------------------------
  const getTodayJST = () => {
    const d = new Date();
    d.setHours(d.getHours() + 9); // JSTに補正
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // [エクセル出力] ボタンが押されました！の処理 -----------------------------------
  const handleExportExcel = () => {
    if (!users.length) {
      setConfirmConfig({
        message: "ユーザーリストが空です。",
        onConfirm: () => setConfirmConfig(null),
      });
      return;
    }

    // ID順にソート
    const sorted = [...users].sort((a, b) => a.id.localeCompare(b.id));

    // 表データを組み立て
    const header = ["ID", "名前", "読みがな", "会社名", "権限", "メモ"];
    const data = sorted.map((u) => [
      u.id, u.name, u.yomigana, u.company, u.role, u.note,
    ]);

    // シート作成
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);

    // 列幅を個別指定
    ws["!cols"] = [
      { wpx: 110 }, // ID
      { wpx: 150 }, // 名前
      { wpx: 150 }, // 読みがな
      { wpx: 150 }, // 会社名
      { wpx: 50 },  // 権限
      { wpx: 200 }, // メモ
    ];

    // ワークブック作成
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ユーザーリスト");

    // 保存
    XLSX.writeFile(wb, `UserList_${getTodayJST()}.xlsx`);
  };

  // [変更登録] ボタンが押されたぞ！の処理 --------------------------------------
  const handleSaveChanges = () => {
    setConfirmConfig({
      message: "変更を登録します。よろしいですか？",
      onConfirm: async () => {
        try {
          const res = await fetch("https://newells4610.stars.ne.jp/api/update_userList.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ users }),
          });
          const json = await res.json();
          if (!res.ok || json.status !== "ok") throw new Error(json.message || "保存に失敗しました");

          // 保存後に再読み込み
          const res2 = await fetch("https://newells4610.stars.ne.jp/data/userList.json", { cache: "no-store" });
          const newUsers = await res2.json();
          const sorted = [...newUsers].sort((a, b) => a.id.localeCompare(b.id));
          setUsers(sorted);
          setOriginalUsers(sorted);
          setSelectedChanges({}); // 変更マークをリセット

          setConfirmConfig({
            message: "変更を登録できました！✨",
            onConfirm: () => setConfirmConfig(null),
          });

        } catch (err) {
          setConfirmConfig({ message: `保存エラーです！: ${err.message}`, onConfirm: () => setConfirmConfig(null) });
        }
      },
      onCancel: () => setConfirmConfig(null),
    });
  };

  // ----------------------------------------------------------------------------


  // --- クリック（フォーカス）で強制的に「変更ありマーク」をつける補助 ---
  const markChanged = (index) => {
    setSelectedChanges(prev => ({ ...(prev || {}), [index]: true }));
  };

  // IDチェック（KDU93103001000 以外は利用不可）
  useEffect(() => {
    if (currentUser?.userId !== "KDU93103001000") {
      setConfirmConfig({
        message: "利用できません。",
        onConfirm: () => {
          setConfirmConfig(null);
          window.location.href = "/"; // ログイン画面に戻す
        },
      });
    } else {
      fetch("https://newells4610.stars.ne.jp/data/userList.json", { cache: "no-store" })
        .then((res) => res.json())
        .then((data) => {
          const sorted = [...data].sort((a, b) => a.id.localeCompare(b.id));
          setUsers(sorted);
          setOriginalUsers(sorted);
        })
        .catch((err) => {
          console.error("ユーザーリスト読み込み失敗:", err);
          setUsers([]);
          setOriginalUsers([]);
        });
    }
  }, [currentUser]);

  // セル幅指定できるカラム
  const columnStyles = {
    mark: { width: "70px", textAlign: "center" },
    id: { width: "110px" },
    name: { width: "170px" },
    yomigana: { width: "170px" },
    company: { width: "170px" },
    role: { width: "50px", textAlign: "center" },
    note: { width: "160px" },
  };

  const handleChange = (index, field, value) => {
    const newUsers = [...users];
    newUsers[index][field] = value;
    setUsers(newUsers);

    // 変更確認マーク更新（元データと全項目比較）
    const changed =
      JSON.stringify(newUsers[index]) !== JSON.stringify(originalUsers[index]);
    setSelectedChanges(prev => ({ ...(prev || {}), [index]: changed }));
  };

  const toggleRole = (index) => {
    const newUsers = [...users];
    // 3段階トグル：管理者 → 利用者 → 発注のみ → 管理者
    if (newUsers[index].role === "管理者") {
      newUsers[index].role = "利用者";
    } else if (newUsers[index].role === "利用者") {
      newUsers[index].role = "発注のみ";
    } else {
      newUsers[index].role = "管理者";
    }
    setUsers(newUsers);

    // 権限切替は確実に変更扱いにする
    setSelectedChanges(prev => ({ ...(prev || {}), [index]: true }));
  };

  // --- 画面 -------------------------------------------------------------------------
  return (
    <div className="modal-overlay">
      <div className="modal-box large">
        <h4 className="modal-title">ユーザーリスト編集</h4>

        {/* ルール表示 */}
        <div className="rules-text">
          データの入力と取り扱いのルール<br /><br />
          　　(1) 【重要】とにかく他のデータを参照して同じように書いてください。<br />
          　　(2) 【重要】[ID]は本人を含む誰にも絶対に教えないこと。QRを渡すだけにしてください。<br />
          　　(3)　アルファベット・数字・記号は半角文字です。<br />
          　　(4)　[ID]は(KDU93103)から始まります。その次の6桁は、上の3桁が会社識別、下3桁が個人識別です。<br />
          　　(5)　[名前]と[読みがな]は苗字と名前の間は半角スペースです。[名前]を(未設定)にしておくと使われません。(ログインできない)<br />
          　　(6)　[会社名]はその人が所属する会社名です。個人契約なら空白です。<br />
          　　(7)　[権限]は(管理者)と(利用者)の別です。基本は全員(利用者)です。<br />
          　　(8)　[メモ]はユーザーリストにつけるメモです。今のところあくまでもメモでしか利用していません。<br />
          　　(9)　辞めた人のところは[名前]を(未設定)に、[権限]を(利用者)に書き換えて。入った人にはそこを利用してください。むやみに増やさない。
        </div>

        {/* 表 ---------------------------------------------------------- */}
        <div className="table-wrapper">
          <table className="order-table">
            <thead>
              <tr>
                <th style={columnStyles.mark}>変更あり？</th>
                <th style={columnStyles.id}>ID</th>
                <th style={columnStyles.name}>名前</th>
                <th style={columnStyles.yomigana}>読みがな</th>
                <th style={columnStyles.company}>会社名</th>
                <th style={columnStyles.role}>権限</th>
                <th style={columnStyles.note}>メモ</th>
              </tr>
            </thead>
            <tbody>
              {users.map((row, idx) => (
                <tr key={idx}>
                  <td style={columnStyles.mark}>
                    <span className={selectedChanges[idx] ? "change-dot changed" : "change-dot"} />
                  </td>
                  <td style={columnStyles.id}>
                    {/* IDは編集不可にする（コピーはできる） */}
                    <input
                      type="text"
                      value={row.id}
                      readOnly
                      className="readonly-input"
                    />
                  </td>
                  
                  <td style={columnStyles.name}>
                    <input
                      type="text"
                      value={row.name}
                      onFocus={() => markChanged(idx)}
                      onChange={(e) => {
                        handleChange(idx, "name", e.target.value)
                        markChanged(idx); // 入力しても緑丸を維持
                      }}
                      style={{ color: row.name === "未設定" ? "#08f" : "inherit" }}
                    />
                  </td>
                  
                  <td style={columnStyles.yomigana}>
                    <input
                      type="text"
                      value={row.yomigana}
                      onFocus={() => markChanged(idx)}
                      onChange={(e) => {
                        handleChange(idx, "yomigana", e.target.value)
                        markChanged(idx); // 入力しても緑丸を維持
                      }}
                    />
                  </td>
                  
                  <td style={columnStyles.company}>
                    <input
                      type="text"
                      value={row.company}
                      onFocus={() => markChanged(idx)}
                      onChange={(e) => {
                        handleChange(idx, "company", e.target.value)
                        markChanged(idx); // 入力しても緑丸を維持
                      }}
                    />
                  </td>
                  
                  <td
                    style={{
                      ...columnStyles.role,
                      color:
                        row.role === "管理者"
                          ? "red"
                          : row.role === "発注のみ"
                          ? "blue"
                          : "black",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleRole(idx)}
                  >
                    {row.role}
                  </td>
                  
                  <td style={columnStyles.note}>
                    <input
                      type="text"
                      value={row.note}
                      onFocus={() => markChanged(idx)}
                      onChange={(e) => {
                        handleChange(idx, "note", e.target.value)
                        markChanged(idx); // 入力しても緑丸を維持
                      }}
                    />
                  </td>
                  
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ボタン群 ------------------------------------------------------- */}
        <div className="modal-actions">
          <button
            style={{ background: "#faa" }}
            onClick={() => setShowAddUserModal(true)}
          >
            ＋ 新規登録
          </button>

          <button onClick={handleExportExcel}>エクセル出力</button>

          <button style={{ background: "#aff" }} onClick={handleSaveChanges}>変更登録</button>
          
          <button
            onClick={() => {
              const hasChanges = Object.values(selectedChanges).some((v) => v);
              if (hasChanges) {
                setConfirmConfig({
                  message: "変更箇所があるよ？このまま破棄して閉じても大丈夫？",
                  onConfirm: () => {
                    setConfirmConfig(null);
                    onClose();
                  },
                  onCancel: () => setConfirmConfig(null),
                });
              } else {
                onClose();
              }
            }}
          >
            閉じる
          </button>
          
        </div>

      {/* 新規登録モーダル --------------------------------------------- */}

        {showAddUserModal && (
          <div className="modal-overlay">
            <div className="modal-box small" style={{ backgroundColor: "#fff" }}>
              <h3>新規ユーザー登録</h3>
              <table className="adduser-table">
                <tbody>
                  <tr>
                    <td>ID</td>
                    <td>
                      <span>KDU93103</span>
                      <input
                        type="text"
                        value={newUser.suffix}
                        placeholder="123456"
                        maxLength={6}
                        style={{ width: "80px" }}
                        onChange={(e) =>
                          setNewUser({ ...newUser, suffix: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>名前</td>
                    <td>
                      <input
                        type="text"
                        value={newUser.name}
                        style={{ width: "240px" }} // 全角15文字程度
                        onChange={(e) =>
                          setNewUser({ ...newUser, name: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>読みがな</td>
                    <td>
                      <input
                        type="text"
                        value={newUser.yomigana}
                        style={{ width: "240px" }}
                        onChange={(e) =>
                          setNewUser({ ...newUser, yomigana: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>会社名</td>
                    <td>
                      <input
                        type="text"
                        value={newUser.company}
                        style={{ width: "240px" }}
                        onChange={(e) =>
                          setNewUser({ ...newUser, company: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>メモ</td>
                    <td>
                      <input
                        type="text"
                        value={newUser.note}
                        style={{ width: "240px" }}
                        onChange={(e) =>
                          setNewUser({ ...newUser, note: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="modal-buttons">
                <button
                  onClick={async () => {
                    // 入力チェック
                    if (!/^\d{6}$/.test(newUser.suffix)) {
                      setConfirmConfig({
                        message: "IDの末尾は半角数字6桁で入力してください！！",
                        onConfirm: () => setConfirmConfig(null),
                        hideCancel: true,
                      });
                      return;
                    }

                    const userToSave = {
                      id: newUser.id + newUser.suffix,
                      name: newUser.name,
                      yomigana: newUser.yomigana,
                      company: newUser.company,
                      role: newUser.role,
                      note: newUser.note,
                    };
                    try {
                      const res = await fetch(
                        "https://newells4610.stars.ne.jp/api/add_userList.php",
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(userToSave),
                        }
                      );
                      const json = await res.json();
                      if (!res.ok || json.status !== "ok")
                        throw new Error(json.message || "登録に失敗しました");

                      setConfirmConfig({
                        message: "新規登録できました！✨",
                        onConfirm: async () => {
                          setConfirmConfig(null);
                          setShowAddUserModal(false);
                          setNewUser(initialNewUser); // 初期化

                          // 再読み込みして一覧更新
                          const res2 = await fetch(
                            "https://newells4610.stars.ne.jp/data/userList.json",
                            { cache: "no-store" }
                          );
                          const data = await res2.json();
                          const sorted = [...data].sort((a, b) =>
                            a.id.localeCompare(b.id)
                          );
                          setUsers(sorted);
                          setOriginalUsers(sorted);
                          setSelectedChanges({});
                        },
                        hideCancel: true,
                      });
                    } catch (err) {
                      setConfirmConfig({
                        message: `保存エラーです！: ${err.message}`,
                        onConfirm: () => setConfirmConfig(null),
                      });
                    }
                  }}
                >
                  登録
                </button>
                <button onClick={() => {
                  setShowAddUserModal(false);
                  setNewUser(initialNewUser); // 初期化
                }}>
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 確認用モーダル ---------------------------------------------------- */}
        
        {confirmConfig && (
          <div className="modal-overlay">
            <div className="modal-box small">
              <p>{confirmConfig.message}</p>
              <div className="modal-actions center">
                <button onClick={confirmConfig.onConfirm}>OK</button>
                {confirmConfig.onCancel && <button onClick={confirmConfig.onCancel}>キャンセル</button>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserListEditModal;
